import { useApolloClient, useMutation } from '@apollo/client';
import type { UseEditOrganization } from '@openx/components-organization/lib/utils/context/OrganizationHookTypes.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import {
  UpdateOrganizationDocument,
  type UpdateOrganizationMutationVariables,
} from '../../../graphql/mutation/organizations/UpdateOrganization.generated.tsx';
import { GetOrganizationDocument } from '../../../graphql/query/organizations/GetOrganization.generated.tsx';

export const useEditOrganization: UseEditOrganization = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  const sessionAccountId = session?.account?.id;

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();

  const [updateOrganizationMutation, { loading }] = useMutation(UpdateOrganizationDocument, {
    awaitRefetchQueries: true,
    onCompleted: ({ account }) => {
      apolloClient.cache.evict({
        fieldName: 'account',
        broadcast: false,
      });

      const navigatePath = account?.id ? `/organizations/${account.id}/details` : '/organizations';
      sessionAccountId === account?.id && apolloClient.cache.evict({ fieldName: 'getSession', id: 'ROOT_QUERY' });
      apolloClient.cache.gc();

      enqueueSnackbar(t('Organization edited successfully'), { variant: 'success' });
      navigate(navigatePath);
    },
    onError: ({ graphQLErrors }) => {
      const defaultErrorMessage = t('Failed to edit organization');
      const message = graphQLErrors[0]?.message;

      enqueueSnackbar(message ?? defaultErrorMessage, { variant: 'error' });
    },
    refetchQueries: ['GetOrganizationsList'],
  });

  const editOrganization = useCallback(
    async (formData: UpdateOrganizationMutationVariables) => {
      await updateOrganizationMutation({
        refetchQueries: [{ query: GetOrganizationDocument, variables: { id: formData.id } }],
        variables: formData,
      });
    },
    [updateOrganizationMutation]
  );

  return { editOrganization, loading };
};
